<template lang="html">
  <div>
    <div v-if="showData">
      <div :class="playerClass" class="player">
        <SoundslidesPlayer
          v-if="audio_ready"
          ref="editPlayer"
          :path_to_assets="path_to_assets"
          :editing_initial_slide_data="slideArray"
          :audio_source="audio_source"
          :force_preload="true"
          @onSlideChange="slideChange"
        />
      </div>
      <Grid
        v-show="show_grid"
        ref="grid"
        :path_to_assets="path_to_assets"
        :slide_array="slideArray"
        :slide_index="currentSlideIndex"
        class="grid"
        @onOrderChange="slideArrayOrderChanged"
      />
      <audio id="audioElement" controls controlsList="nodownload">
        <source :src="audioUrl" type="audio/mpeg" />
      </audio>
      <PeakOverlay
        v-show="peak_ready"
        id="overlayContainer"
        ref="peakOverlay"
        :zoom_pixels="zoomTimePixels"
        :zoom_offset="zoomTimeOffsetPixels"
        :slide_array="slideArray"
        :slide_index="currentSlideIndex"
        @onOrderChange="slideArrayOrderChanged"
      />
      <div id="peakContainer" />
      <BasicControls
        @zoomIn="zoomIn"
        @zoomOut="zoomOut"
        @playToggle="playToggle"
        @rewind="rewind"
        @scrubRewind="rewind(3)"
        @scrubForward="forward(3)"
        @gridToggle="toggleGrid"
        @gridThumbLarger="gridThumbSize(1.1)"
        @gridThumbSmaller="gridThumbSize(0.9)"
        @timelineThumbLarger="timelineThumbSize(1.1)"
        @timelineThumbSmaller="timelineThumbSize(0.9)"
      />
    </div>
  </div>
</template>

<script>
import SoundslidesPlayer from "@/components/SoundslidesPlayer/SoundslidesPlayer";
import Grid from "@/components/Editor/Grid";
import BasicControls from "@/components/Editor/BasicControls";
import Peaks from "peaks.js";
import PeakOverlay from "@/components/Editor/PeakOverlay";
import _ from "lodash";
import variables from "@/scss/Editor/_variables.scss";

export default {
  name: "Editor",
  components: {
    SoundslidesPlayer,
    Grid,
    PeakOverlay,
    BasicControls
  },
  data() {
    return {
      audio_ready: false,
      audio_source: null,
      currentSlideIndex: 0,
      frameOffsetWatcherInt: null,
      mini_player: false,
      overviewTimePixels: 0,
      peak_ready: false,
      peakInstance: null,
      pointsDragging: false,
      show_grid: true,
      throttledPushToPlayer: null,
      debouncedSaveToServer: null,
      zoomLevel: 0,
      zoomLevels: [512, 1024, 2048, 4096],
      zoomTimeOffsetPixels: 0,
      zoomTimePixels: 0
    };
  },
  computed: {
    id() {
      return this.$route.params.id;
    },
    showData() {
      return this.$store.state.currentShowDetails.data;
    },
    showUrl() {
      return `https://s3.amazonaws.com/users.soundslides.media/${
        this.$store.state.currentShowDetails.ssid
      }`;
    },
    slideArray() {
      if (this.$store.state.currentShowDetails.data) {
        return this.$store.state.currentShowDetails.data.slideArray;
      } else {
        return [];
      }
    },
    audioUrl() {
      return this.showUrl + "/" + this.showData.audio.filename;
    },
    path_to_assets() {
      return this.showUrl + "/";
    },
    playerClass() {
      if (this.mini_player && !this.show_grid) {
        return "player_fluid_25";
      }
      if (this.show_grid) {
        return "player_fluid_50";
      } else {
        return "player_fluid_100";
      }
    }
  },
  watch: {
    showData() {
      if (!this.audio_ready) {
        // First time set.
        this.$nextTick(() => {
          this.initialSetup();
        });
      }
    },
    slideArray() {
      if (!this.peak_ready) {
        return false;
      }
      // How to tell if change came from outside or inside?
      if (!this.pointsDragging) {
        // slideArray was modified outside of Editor (from Grid most likely).
        this.setMarkers();
      }
    }
  },
  activated() {
    // Only needed because of keep-alive on rounter view.
    // This is lame, figure it out later.
    document.body.style.backgroundColor = variables.editor_body;
    if (!this.audio_ready) {
      // Check to see if this needs to be reactivated.
      this.$nextTick(() => {
        this.initialSetup();
      });
    }
  },
  deactivated() {
    // Only needed because of keep-alive on rounter view.
    this.peakInstance.player.pause();
  },
  mounted() {
    console.log("mounted");
    document.body.style.backgroundColor = variables.editor_body;
    this.initialSetup();
    //this.$store.commit("setEditingActiveShowId", this.id);
    //this.$store.dispatch("loadShows");
    // Throttle sending data back into the player, or you'll get Tween errors.
    var pushToPlayerThrottleMs = 1000;
    this.throttledPushToPlayer = _.throttle(
      this.pushSlideArrayToPlayer,
      pushToPlayerThrottleMs
    );
    var saveToServerDelayMs = 2000;
    this.debouncedSaveToServer = _.debounce(
      this.saveToServer,
      saveToServerDelayMs
    );
    this.frameOffsetWatcherInt = setInterval(() => {
      if (this.peak_ready) {
        if (
          this.zoomTimeOffsetPixels !=
          this.peakInstance.waveform.waveformZoomView.frameOffset
        ) {
          this.zoomTimeOffsetPixels = this.peakInstance.waveform.waveformZoomView.frameOffset;
          this.setZoomOffset();
        }
      }
    }, 100);
  },
  beforeDestroy() {
    clearInterval(this.frameOffsetWatcherInt);
    this.peakInstance.destroy();
  },
  methods: {
    initialSetup() {
      this.audio_source = this.$el.querySelector("#audioElement");
      this.audio_ready = true;
      console.log("Peak init");
      this.peakInstance = Peaks.init({
        container: this.$el.querySelector("#peakContainer"),
        mediaElement: this.audio_source,
        audioContext: new AudioContext(),
        zoomWaveformColor: variables.zoom_waveform_color,
        overviewWaveformColor: variables.overview_waveform_color,
        overviewHighlightRectangleColor: variables.overview_highlight_color,
        playheadColor: variables.playhead_color,
        playheadTextColor: variables.playhead_text_color,
        showPlayheadTime: false, // Not sure about this.
        axisGridlineColor: variables.timeline_time_grid_color,
        axisLabelColor: variables.timeline_time_text_color
      });
      // Peaks Ready!
      this.peakInstance.on("peaks.ready", () => {
        console.log("peaks.ready");
        this.peak_ready = true;
        this.updatePeakMeasurements();
        this.setMarkers();
      });

      this.peakInstance.on("points.dragstart", () => {
        this.pointsDragging = true;
      });
      this.peakInstance.on("points.dragend", () => {
        this.updateAllPoints();
        this.pointsDragging = false;
      });
      var throttledUpdateAllPoints = _.throttle(this.updateAllPoints, 100);
      this.peakInstance.on("points.dragmove", throttledUpdateAllPoints);

      // Debounce the user seek.
      var _throttled_user_seek = _.throttle(this.setZoomOffset, 100);
      this.peakInstance.on("user_seek", _throttled_user_seek);
      this.peakInstance.on("user_scroll.zoomview", _throttled_user_seek);
      this.peakInstance.on("player_seek", () => {
        console.log("player_seek");
      });
      this.peakInstance.on("waveform_ready.zoomview", () => {
        console.log("waveform_ready.zoomview");
      });
      this.peakInstance.on(
        "zoom.update",
        (currentZoomLevel, previousZoomLevel) => {
          console.log("zoom.update");
          console.log(currentZoomLevel, previousZoomLevel);
          this.zoomLevel = currentZoomLevel;
          this.$nextTick(this.updatePeakMeasurements);
        }
      );
    },
    zoomIn() {
      this.peakInstance.zoom.zoomIn();
      this.setZoomOffset();
    },
    zoomOut() {
      this.peakInstance.zoom.zoomOut();
      this.setZoomOffset();
    },
    playToggle() {
      if (this.peakInstance.player.isPlaying()) {
        this.peakInstance.player.pause();
      } else {
        this.peakInstance.player.play();
      }
    },
    forward(seconds) {
      if (seconds) {
        this.peakInstance.player.seek(
          this.peakInstance.player.getCurrentTime() + seconds
        );
      }
    },
    rewind(seconds) {
      if (seconds) {
        this.peakInstance.player.seek(
          this.peakInstance.player.getCurrentTime() - seconds
        );
      } else {
        this.peakInstance.player.seek(0);
      }
    },
    toggleGrid() {
      this.show_grid = !this.show_grid;
      this.$nextTick(() => {
        this.$refs["editPlayer"].setDimensionValues();
      });
    },
    gridThumbSize(amount) {
      console.log("gridThumbSize", amount);
      this.$refs["grid"].adjustThumbSize(amount);
    },
    timelineThumbSize(amount) {
      console.log("timelineThumbSize", amount);
      this.$refs["peakOverlay"].adjustThumbSize(amount);
    },
    slideChange(idx) {
      // This method is called from player's emitter.
      this.currentSlideIndex = idx;
    },
    setZoomOffset() {
      //console.log("offset", this.peakInstance.waveform.waveformZoomView.frameOffset);
      this.zoomTimeOffsetPixels = this.peakInstance.waveform.waveformZoomView.frameOffset;
    },
    updatePeakMeasurements() {
      this.zoomTimePixels = this.peakInstance.waveform.waveformZoomView.timeToPixels(
        1
      );
      this.overviewTimePixels = this.peakInstance.waveform.waveformOverview.timeToPixels(
        1
      );
    },
    updateAllPoints() {
      // Callback for a point change.
      // console.log("updateAllPoints");
      var points = this.peakInstance.points.getPoints();
      var modifiedArray = _.clone(this.slideArray);
      _.each(modifiedArray, s => {
        var point = _.find(points, { labelText: s.file });
        var timing = point.time;
        s.timing = timing * 1000;
      });
      modifiedArray = _.sortBy(modifiedArray, "timing");
      this.saveSlideArray(modifiedArray);
      this.throttledPushToPlayer();
    },
    setMarkers() {
      // This run when the peaks is first ready AND whenever slideArray is
      // changed from "OUTSIDE" (usually from Grid via vuex).
      // Labels are overridden from within Peak.js.
      // See modification of 'showLabel' to false WITHIN node_modules (yes, huge hack!);
      this.peakInstance.points.removeAll();
      // Add markers.
      var markerBodyColor = variables.timeline_marker_color;
      var new_points = _.map(this.slideArray, s => {
        return {
          time: s.timing / 1000,
          labelText: `${s.file}`,
          color: markerBodyColor,
          editable: true
        };
      });
      // Make first point uneditable.
      // First time is always zero.
      new_points[0].editable = false;
      this.peakInstance.points.add(new_points);
    },
    saveSlideArray(_newArray) {
      // Save to Editor.
      this.$store.commit("saveSlideArrayToShow", {
        showId: this.id,
        slideArray: _newArray
      });
      // Save to server.
      this.debouncedSaveToServer();
    },
    saveToServer() {
      this.$store.dispatch("updateCurrentShow");
    },
    slideArrayOrderChanged(changedSlideArray) {
      // Called from Grid or PeakOverlay, when user changes order of slides.
      // Save the existing timings.
      var existing_timings = _.map(this.slideArray, "timing");
      // Apply existing timings to new slide order (aka changedSlideArray);
      changedSlideArray = _.map(changedSlideArray, (s, idx) => {
        s.timing = existing_timings[idx];
        return s;
      });
      // Save to Editor.
      this.saveSlideArray(changedSlideArray);
      // Push into SoundslidesPlayer.
      this.throttledPushToPlayer();
    },
    pushSlideArrayToPlayer() {
      // Push into SoundslidesPlayer.
      // Slide array within the player is not attached to Vuex.
      // The Player is completely reactive so that not to require
      // Vuex when deployed separately.
      this.$refs["editPlayer"].setSlideData(this.slideArray);
    }
  }
};
</script>

<style lang="scss">
@import "@/scss/Editor/_variables.scss";

body {
  //background-color: $editor-body;
}
#player {
  position: fixed;
  left: 20px;
  top: 42px;
}
#audioElement {
  // Offscreen, forever!
  position: fixed;
  top: -500px;
  left: 60px;
}
#peakContainer {
  //background-color: grey;
  position: fixed;
  padding-top: 10px;
  padding-bottom: 40px;
  bottom: 0px;
  left: 1%;
  width: 98%;
  //height: 70%;
  .waveform {
    .zoom-container {
      background-color: $timeline-main-background;
      box-shadow: 0px 0px 4px black;
    }
    .overview-container {
      margin-top: 18px;
      height: 70px;
      background-color: $timeline-overview-background;
      box-shadow: 1px 1px 4px black;
      margin-bottom: 10px;
    }
  }
}

#overlayContainer {
  position: absolute;
  width: 98%;
  left: 1%;
  height: 0px;
  margin-bottom: 940px;
  bottom: 0;
  background-color: pink;
  //overflow: auto;
  //overflow-x: hidden;
  z-index: -1;
  //box-shadow: 0px 0px 4px #999;
}
.player {
  box-shadow: 2px 2px 4px #222;
}
.player_fluid_25 {
  position: absolute;
  top: 20px;
  left: 1%;
  height: 200px;
  width: 266px;
}

.player_fluid_50 {
  position: absolute;
  top: 20px;
  left: 1%;
  width: 48%;
  height: 50%;
}
.player_fluid_100 {
  position: absolute;
  top: 0px;
  left: 0;
  width: 100%;
  height: 33%;
}
.grid {
  position: absolute;
  left: 50%;
  width: 50%;
  top: 16px;
  //background-color: red;
}
</style>
